import classNames from 'classnames';
import deepmerge from 'deepmerge';
import {ComponentProps, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BackgroundAnimation from '../../../resources/animations/Background.json';
import Dryshire from '../../../resources/animations/Dryshire.json';
import MiscanthusSmall from '../../../resources/animations/Miscanthus_Small.json';
import MiscanthusWhole from '../../../resources/animations/Miscanthus_Whole.json';
import Rainyshire from '../../../resources/animations/Rainyshire.json';
import Sunnyshire from '../../../resources/animations/Sunnyshire.json';
import Tractor from '../../../resources/animations/Tractor.json';
import Willow1 from '../../../resources/animations/Willow_1.json';
import Willow2 from '../../../resources/animations/Willow_2.json';
import Willow3 from '../../../resources/animations/Willow_3.json';
import Windyshire from '../../../resources/animations/Windyshire.json';
import {CropStumpsMiscanthus, CropStumpsWillow} from '../../../resources/Icons';
import Col from '../../common/Col';
import Row from '../../common/Row';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {GameLogicError} from '../errors';
import {useCurrentEvent, useGameStatus, useGrowAnimationYear} from '../hooks';
import {CropId} from '../resources/crops';
import {PlaceId} from '../resources/places';
import {GamePhase} from '../types';
import GameAudio, {GameAudioHandle} from './GameAudio';
import LottieAnimation from './LottieAnimation';

type Environment = {
  background: object;
  music: string;
};

const WillowAnimation = [Willow1, Willow2, Willow3];
const MiscanthusAnimation = [MiscanthusWhole];

const environments: Record<'initial' | PlaceId, Environment> = {
  initial: {background: BackgroundAnimation, music: 'sounds/dryshire.mp3'},
  [PlaceId.dryshire]: {background: Dryshire, music: 'sounds/dryshire.mp3'},
  [PlaceId.sunnyshire]: {background: Sunnyshire, music: 'sounds/dryshire.mp3'},
  [PlaceId.rainyshire]: {background: Rainyshire, music: 'sounds/rainyshire.mp3'},
  [PlaceId.windyshire]: {background: Windyshire, music: 'sounds/windyshire.mp3'},
};

const BASE_BOTTOM_DISTANCE = 6.8; // %
const CROP_HEIGHT = 6; // %
const N_CROPS_WEB = 11;
const N_CROPS_MOBILE = 5;

const STEM_CLASS = 'is-crop-stem';

const Background = ({className, ...props}: ComponentProps<'div'>) => {
  const gameStatus = useGameStatus();
  const cropsGrowingAudio = useRef<GameAudioHandle>(null);
  const tractorAudio = useRef<GameAudioHandle>(null);
  const {isMobile} = useBreakpoint('mobile');
  const tractorRef = useRef<HTMLDivElement>(null);
  const [hiddenStems, setHiddenStems] = useState<boolean[][]>([]);
  const [showEventAnimation, setShowEventAnimation] = useState(false);
  const [backgroundStack, setBackgroundStack] = useState<Environment['background'][]>([
    environments.initial.background,
    environments.initial.background,
  ]);
  const currentBackground = useRef(0);

  const {currentEvent} = useCurrentEvent();

  const cropsPerRow = isMobile ? N_CROPS_MOBILE : N_CROPS_WEB;

  const environment = useMemo(
    () => (gameStatus.place ? environments[gameStatus.place.id] : environments['initial']),
    [gameStatus.place]
  );

  const updateBackground = useCallback((newBackground: Environment['background']) => {
    GameLogicError.assertNonNullish([0, 1].includes(currentBackground.current));
    if (currentBackground.current === 0) {
      setBackgroundStack(stack => [stack[0], newBackground]);
      currentBackground.current = 1;
    } else {
      setBackgroundStack(stack => [newBackground, stack[1]]);
      currentBackground.current = 0;
    }
  }, []);

  useEffect(() => updateBackground(environment.background), [environment.background, updateBackground]);

  const animationYear = useGrowAnimationYear();

  const _getCurrentAnimation = useCallback(
    (animation: any[]): object | undefined => {
      if (animationYear < 0) return undefined;
      if (animationYear >= animation.length) return animation[animation.length - 1];
      return {...animation[animationYear]};
    },
    [animationYear]
  );

  const youngCropAnimation = useMemo(() => {
    return gameStatus.harvestYears.length === 0 && gameStatus.crop?.id === CropId.miscanthus
      ? MiscanthusSmall
      : undefined;
  }, [gameStatus.crop?.id, gameStatus.harvestYears.length]);

  const cropAnimation = useMemo(() => {
    if (!gameStatus.crop) return null;
    if (gameStatus.crop.id === CropId.miscanthus) return _getCurrentAnimation(MiscanthusAnimation);
    if (gameStatus.crop.id === CropId.willow) return _getCurrentAnimation(WillowAnimation);
    return null;
  }, [_getCurrentAnimation, gameStatus.crop]);

  // Reset fade in effect whenever event animation changes
  useEffect(() => {
    if (currentEvent?.animation) setShowEventAnimation(false);
  }, [currentEvent?.animation]);

  const {eventAnimation, eventAnimationPosition} = useMemo(() => {
    const AnimationComponent = currentEvent?.animation;
    let eventAnimation = undefined;
    if (AnimationComponent) {
      eventAnimation = (
        <div className={classNames(showEventAnimation ? 'transition-opacity duration-1000 opacity-100' : 'opacity-0')}>
          <AnimationComponent />
        </div>
      );
      setTimeout(() => setShowEventAnimation(true), 50);
    }
    const eventAnimationPosition = currentEvent?.animationPosition ?? 'foreground';
    return {eventAnimation, eventAnimationPosition};
  }, [currentEvent?.animation, currentEvent?.animationPosition, showEventAnimation]);

  const randomnessMap = useMemo(() => {
    return [cropsPerRow, cropsPerRow - 1].map(nCrops =>
      Array(nCrops)
        .fill(null)
        .map(_ => ({
          inverted: Math.random() < 0.5, // 50% probability
          offset: Math.random() * 4 - 1, // [-3 to 1] rem
        }))
    );
  }, [cropsPerRow]);

  const buildMap = useCallback(
    (renderElement: (inverted: boolean, offset: number, colIdx: number, rowIdx: number) => JSX.Element) => {
      return [cropsPerRow, cropsPerRow - 1].map((nCrops, rowIdx) => (
        <Row
          key={`crop_row_${rowIdx}`}
          className="absolute w-full justify-center"
          style={{bottom: `${BASE_BOTTOM_DISTANCE + CROP_HEIGHT * rowIdx}%`}}
        >
          {Array(nCrops)
            .fill(null)
            .map((_, colIdx) => {
              const {inverted, offset} = randomnessMap[rowIdx][colIdx];
              return renderElement(inverted, offset, colIdx, rowIdx);
            })}
        </Row>
      ));
    },
    [cropsPerRow, randomnessMap]
  );

  const buildCropMap = useCallback(
    (animation: object) => {
      return buildMap((inverted, offset, colIdx, rowIdx) => (
        <div key={`crop_${rowIdx}_${colIdx}`} className="relative">
          <LottieAnimation
            data-row={rowIdx}
            data-col={colIdx}
            className={classNames(STEM_CLASS, 'relative w-[16rem] h-[27rem]', {'-scale-x-100': inverted})}
            animation={animation}
            style={{marginLeft: `${offset}rem`, opacity: hiddenStems[rowIdx]?.[colIdx] ? 0 : 1}}
          />
          {eventAnimationPosition === 'stem' && eventAnimation}
        </div>
      ));
    },
    [buildMap, eventAnimation, eventAnimationPosition, hiddenStems]
  );

  const cropMap = useMemo(() => {
    return cropAnimation ? buildCropMap(cropAnimation) : null;
  }, [buildCropMap, cropAnimation]);

  const youngCropMap = useMemo(() => {
    return youngCropAnimation ? buildCropMap(youngCropAnimation) : null;
  }, [buildCropMap, youngCropAnimation]);

  const cropStumps = useMemo(() => {
    if (!gameStatus.crop) return null;
    const CropStumpsImage = gameStatus.crop.id === CropId.miscanthus ? CropStumpsMiscanthus : CropStumpsWillow;
    return buildMap((inverted, offset, colIdx, rowIdx) => (
      <Col
        key={`crop_${rowIdx}_${colIdx}`}
        className={classNames('w-[16rem] h-[27rem] justify-end', {'-scale-x-100': inverted})}
        style={{
          marginLeft: `${offset}rem`,
        }}
      >
        <CropStumpsImage
          className={'w-full h-full flex flex-col justify-end'}
          style={{
            opacity: gameStatus.isHarvesting
              ? hiddenStems[rowIdx]?.[colIdx]
                ? 1
                : 0
              : cropMap || youngCropMap
              ? 0
              : 1,
          }}
        />
      </Col>
    ));
  }, [buildMap, cropMap, gameStatus.crop, gameStatus.isHarvesting, hiddenStems, youngCropMap]);

  const harvestTractor = useMemo(() => {
    if (!gameStatus.isHarvesting) return null;
    tractorAudio.current?.play();
    return (
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
        <LottieAnimation ref={tractorRef} className="w-[120rem] animate-move-truck" animation={Tractor} loop={false} />
      </div>
    );
  }, [gameStatus.isHarvesting]);

  useEffect(() => {
    if (cropAnimation || youngCropAnimation) cropsGrowingAudio.current?.play();
  }, [cropAnimation, youngCropAnimation]);

  useEffect(() => {
    if (!gameStatus.isHarvesting) {
      setHiddenStems([]);
      return;
    }

    const intervalId = setInterval(() => {
      const tractorRect = tractorRef.current?.getBoundingClientRect();
      if (!tractorRect) return;
      // Hide crop stems as tractor progresses
      const stems = document.getElementsByClassName(STEM_CLASS);

      setHiddenStems(prevState => {
        const copy = deepmerge({}, prevState);
        let allHidden = true;
        for (const stem of stems) {
          const stemRect = stem.getBoundingClientRect();
          const {row, col} = (stem as HTMLElement).dataset;
          if (row === undefined || col === undefined) continue;
          const isHidden = tractorRect.left + tractorRect.width / 2 >= stemRect.left + stemRect.width;
          if (!copy[parseInt(row)]) copy[parseInt(row)] = []; // Initialise row array if necessary
          copy[parseInt(row)][parseInt(col)] = isHidden;
          if (!isHidden) allHidden = false;
        }
        if (allHidden) clearInterval(intervalId);
        return copy;
      });
    }, 100);

    return () => clearInterval(intervalId);
  }, [gameStatus.isHarvesting]);

  /**
   * On background GameAudio both play and autoPlay are needed in order to:
   * - Sound initially
   * - Sound automatically when the audio changes
   * - Sound again when the game is unmuted
   */
  return (
    <>
      <GameAudio src={environment.music} volume={0.4} play={true} autoPlay={true} loop={true} />
      <div
        className={classNames(
          'relative h-full w-full bg-cover bg-no-repeat',
          // Leave static bg to ensure a bg is going to be displayed
          gameStatus.place?.bgClass,
          className
        )}
        {...props}
        aria-hidden="true"
      >
        {[0, 1].map(bgIdx => (
          <LottieAnimation
            key={`bgIdx-${bgIdx}`}
            className={classNames(
              'absolute h-full w-full transition-opacity',
              currentBackground.current === bgIdx ? 'opacity-100' : 'opacity-0',
              {'duration-1000': gameStatus.phase > GamePhase.welcome}
            )}
            animation={backgroundStack[bgIdx]}
            loop={true}
          />
        ))}
        <GameAudio ref={cropsGrowingAudio} src="sounds/cropsGrowing.mp3" />
        <GameAudio ref={tractorAudio} src="sounds/tractorEngine.mp3" />
        {eventAnimationPosition === 'background' && eventAnimation}
        {cropStumps}
        {youngCropMap}
        {cropMap}
        {harvestTractor}
        {eventAnimationPosition === 'foreground' && eventAnimation}
      </div>
    </>
  );
};

export default Background;
